import { action, makeObservable, observable } from 'mobx';
import * as yup from 'yup';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod } from '../types';
import { RootStore } from './root';
import { WithNetworkConcurrency } from './with-network-concurrency';

const ProvinceSchema = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
    ageNicotine: yup.number().nullable().required(),
});

const BusinessUnitSchema = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
    provinces: yup.array().of(ProvinceSchema).required(),
});

export const YupBusinessUnit = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
});

export const YupProvince = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
    businessUnit: YupBusinessUnit.required(),
});

export const ResponseProvinceSchema = yup.object({
    id: yup.number().required(),
    province: YupProvince.required(),
});

export type Province = yup.InferType<typeof ProvinceSchema>;
export type BusinessUnit = yup.InferType<typeof BusinessUnitSchema>;

export type ProvinceResponse = yup.InferType<typeof ResponseProvinceSchema>;

const BusinessUnitsArraySchema = yup.array().of(BusinessUnitSchema).required();

export class BusinessUnitStore extends WithNetworkConcurrency {
    rootStore: RootStore;
    @observable businessUnits: BusinessUnit[] | null = null;
    @observable isFetchingBusinessUnits = false;

    constructor(rootStore: RootStore) {
        super();
        this.rootStore = rootStore;
        makeObservable(this);
    }

    getProvinceIdByCode(code: string): number | undefined {
        return this.businessUnits
            ?.flatMap((bu) => bu.provinces)
            .find((p) => p.name === code)?.id;
    }

    @action
    async fetchBusinessUnits() {
        if (this.isFetchingBusinessUnits) {
            return;
        }

        this.isFetchingBusinessUnits = true;
        try {
            const response = await this.rootStore.makeNetworkCall(
                {
                    method: HttpMethod.GET,
                    url: '/business-units',
                },
                BusinessUnitsArraySchema
            );

            if (!response.err) {
                this.businessUnits = response.data;
            }
        } catch (error) {
            NotificationManager.showError('Failed to fetch business units');
            console.error('Error fetching business units:', error);
        } finally {
            this.isFetchingBusinessUnits = false;
        }
    }
}
